import { useEffect, useState } from "react";

import { $isLinkNode, LinkNode } from "@lexical/link";
import { $isListItemNode, $isListNode } from "@lexical/list";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $getSelection,
  ElementNode,
  TextNode,
  $isRangeSelection,
  $isTextNode,
  $isParagraphNode,
  RangeSelection,
  LexicalNode,
  $isElementNode,
} from "lexical";

export const useRichTextToolbarState = () => {
  const [editor] = useLexicalComposerContext();

  const [toolbarState, setToolbarState] = useState({
    isBold: false,
    isItalic: false,
    isUnderline: false,
    isOrderedList: false,
    isUnorderedList: false,
    fontSize: "14",
    textColor: "",
    lineHeight: "",
    isLink: false,
    linkUrl: "",
    textAlign: "left" as "left" | "center" | "right" | "justify" | "mixed",
  });

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const selection = $getSelection();

        if (!$isRangeSelection(selection)) {
          return;
        }

        const listType = getListTypeFromNode(selection.anchor.getNode());
        const fontSize = getFontSizeFromSelection(selection);
        const textColor = getTextColorFromSelection(selection);
        const lineHeight = getLineHeightFromSelection(selection);
        const { isLink, linkUrl } = getLinkFromSelection(selection);
        const textAlign = getTextAlignFromSelection(selection);

        setToolbarState((s) => ({
          ...s,
          isBold: selection.hasFormat("bold"),
          isItalic: selection.hasFormat("italic"),
          isUnderline: selection.hasFormat("underline"),
          isOrderedList: listType === "number",
          isUnorderedList: listType === "bullet",
          fontSize,
          textColor,
          lineHeight,
          isLink,
          linkUrl,
          textAlign,
        }));
      });
    });
  }, [editor]);

  return toolbarState;
};

const getListTypeFromNode = (node: ElementNode | TextNode): string | null => {
  let current: ElementNode | TextNode | null = node;
  while (current !== null) {
    if ($isListNode(current)) {
      return current.getListType();
    }
    current = current.getParent();
  }
  return null;
};

const getFontSizeFromSelection = (selection: RangeSelection): string => {
  const fontSizes = new Set<string>();
  const nodes = selection.getNodes();

  for (const node of nodes) {
    if ($isTextNode(node)) {
      const fontSize = extractFontSizeFromStyle(node.getStyle());
      fontSizes.add(fontSize);
    }
  }

  if (fontSizes.size > 1) {
    return "mixed";
  }
  if (fontSizes.size === 1) {
    return Array.from(fontSizes)[0];
  }
  return "14";
};

const extractFontSizeFromStyle = (style: string | null): string => {
  if (!style) {
    return "14";
  }
  const fontSizeMatch = style.match(/font-size:\s*(\d+)px/i);
  return fontSizeMatch ? fontSizeMatch[1] : "14";
};

const getTextColorFromSelection = (selection: RangeSelection): string => {
  const textColors = new Set<string>();
  const nodes = selection.getNodes();

  for (const node of nodes) {
    if ($isTextNode(node)) {
      const color = extractColorFromStyle(node.getStyle());
      if (color) {
        textColors.add(color);
      }
    }
  }

  if (textColors.size === 0) {
    // Anchor can only be a TextNode/ElementNode, never a chip — the loop above is the sole path for chip colours.
    const anchorColor = extractColorFromStyle(
      $isTextNode(selection.anchor.getNode()) ? selection.anchor.getNode().getStyle() : null
    );
    if (anchorColor) {
      textColors.add(anchorColor);
    }
  }

  if (textColors.size > 1) {
    return "";
  }
  if (textColors.size === 1) {
    return Array.from(textColors)[0];
  }
  return "";
};

// Browsers normalise hex to rgb() on DOM read, so we re-fold both shapes back to lowercase hex for equality checks.
const normalizeColorToHex = (color: string): string => {
  const trimmed = color.trim();

  if (trimmed.startsWith("#")) {
    const hex = trimmed.toLowerCase();
    if (hex.length === 4) {
      return `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`;
    }
    return hex;
  }

  const rgbMatch = trimmed.match(/^rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)$/i);
  if (rgbMatch) {
    const r = parseInt(rgbMatch[1]).toString(16).padStart(2, "0");
    const g = parseInt(rgbMatch[2]).toString(16).padStart(2, "0");
    const b = parseInt(rgbMatch[3]).toString(16).padStart(2, "0");
    return `#${r}${g}${b}`;
  }

  return trimmed;
};

const extractColorFromStyle = (style: string | null): string | null => {
  if (!style) {
    return null;
  }
  const colorMatch = style.match(/(?:^|;\s*)color:\s*([^;]+)/i);
  return colorMatch ? normalizeColorToHex(colorMatch[1].trim()) : null;
};

const findLinkNode = (node: LexicalNode): LinkNode | null => {
  let current: LexicalNode | null = node;
  while (current !== null) {
    if ($isLinkNode(current)) {
      return current;
    }
    current = current.getParent();
  }
  return null;
};

const getLinkFromSelection = (selection: RangeSelection): { isLink: boolean; linkUrl: string } => {
  const nodes = selection.getNodes();
  const anchorNode = selection.anchor.getNode();

  for (const node of nodes) {
    const linkNode = findLinkNode(node);
    if (linkNode) {
      return { isLink: true, linkUrl: linkNode.getURL() || "" };
    }
  }

  const linkNode = findLinkNode(anchorNode);
  if (linkNode) {
    return { isLink: true, linkUrl: linkNode.getURL() || "" };
  }

  return { isLink: false, linkUrl: "" };
};

const getLineHeightFromSelection = (selection: RangeSelection): string => {
  let current: LexicalNode | null = selection.anchor.getNode();
  while (current !== null) {
    if ($isParagraphNode(current) || $isListItemNode(current)) {
      return extractLineHeightFromStyle(current.getStyle());
    }
    current = current.getParent();
  }
  return "";
};

const extractLineHeightFromStyle = (style: string | null): string => {
  if (!style) {
    return "";
  }
  const match = style.match(/line-height:\s*([^;]+)/i);
  return match ? match[1].trim() : "";
};

const getTextAlignFromSelection = (selection: RangeSelection): "left" | "center" | "right" | "justify" | "mixed" => {
  let current: LexicalNode | null = selection.anchor.getNode();
  while (current !== null) {
    if ($isElementNode(current)) {
      const type = current.getType();
      if ($isParagraphNode(current) || type.startsWith("heading") || type === "listitem") {
        const formatType = current.getFormatType();
        if (formatType === "left" || formatType === "center" || formatType === "right" || formatType === "justify") {
          return formatType;
        }
        return "left";
      }
    }
    current = current.getParent();
  }
  return "left";
};
